import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/LoginOutlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import GavelIcon from "@mui/icons-material/GavelOutlined";
import MedicalIcon from "@mui/icons-material/MedicalInformationOutlined";
import FamilyIcon from "@mui/icons-material/Diversity1Outlined";
import VideocamOffIcon from "@mui/icons-material/VideocamOffOutlined";
import VideocamIcon from "@mui/icons-material/VideocamOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  reduceDisplayName,
  type DoorSignIdentityMode,
  type DoorSignStatus,
  type VideoCallSeat,
  type VideoCallSeatSummary,
} from "@shared/types/foyer";
import {
  foyerApi,
  type CareAuditFilter,
  type CareAuditRecord,
  type CareAuditVerification,
  type CareOccupancy,
  type CarePhiReveal,
} from "../http/foyer";
import CollapsiblePanel from "./CollapsiblePanel";
import CareProfileDialog from "./CareProfileDialog";
import FamilyContactsDialog from "./FamilyContactsDialog";
import { PageHeader } from "./PageHeader";

interface Props {
  premiseId: string;
  rooms: { id: string; name?: string }[];
  /** Current premise-wide identity policy (from the draft config). */
  identityMode: DoorSignIdentityMode;
  /** Mirror the policy change into the draft so a later full Save can't clobber
   *  the value the dedicated endpoint just persisted. */
  onIdentityModeChange: (mode: DoorSignIdentityMode) => void;
  /** Changing the policy decrypts + rebuilds resident names, so it needs PHI access. */
  canViewPhi: boolean;
  /** Premise timezone - prefilled into a family contact's quiet hours (VC-11). */
  premiseTimezone?: string;
  /**
   * Admitting fills a room, discharging empties it - the server already flips
   * `Room.occupied` as part of both. This mirrors that into the premise draft
   * so the Rooms tab isn't showing yesterday's answer until a page reload.
   */
  onRoomOccupancyChange?: (roomId: string, occupied: boolean) => void;
}

const STATUS_OPTIONS: { value: DoorSignStatus; label: string }[] = [
  { value: "none", label: "No status" },
  { value: "resting", label: "Resting" },
  { value: "knock", label: "Please knock" },
  { value: "do-not-disturb", label: "Do not disturb" },
  { value: "out", label: "Out" },
];

const SAMPLE_NAME = "Astrid Lund";

const IDENTITY_OPTIONS: {
  value: DoorSignIdentityMode;
  label: string;
}[] = [
  { value: "none", label: "Nothing - room + status only" },
  {
    value: "initials",
    label: `Initials - ${reduceDisplayName(SAMPLE_NAME, "initials")}`,
  },
  {
    value: "first-name",
    label: `First name - ${reduceDisplayName(SAMPLE_NAME, "first-name")}`,
  },
  {
    value: "first-initial",
    label: `First name + initial - ${reduceDisplayName(SAMPLE_NAME, "first-initial")}`,
  },
  {
    value: "full-name",
    label: `Full name - ${reduceDisplayName(SAMPLE_NAME, "full-name")}`,
  },
];

/** Privacy ordering - confirm before moving to a MORE exposing policy. */
const EXPOSURE_RANK: Record<DoorSignIdentityMode, number> = {
  none: 0,
  initials: 1,
  "first-name": 2,
  "first-initial": 3,
  "full-name": 4,
};

/**
 * Care authoring: admit/discharge residents, set per-room door-sign status, and
 * set the premise-wide identity policy. The roster is PHI-free - it shows only
 * the policy-reduced display name (often blank). A name typed at admit is
 * encrypted server-side and never read back here.
 */
export default function ResidentsEditor({
  premiseId,
  rooms,
  identityMode,
  onIdentityModeChange,
  canViewPhi,
  premiseTimezone,
  onRoomOccupancyChange,
}: Props) {
  const [roster, setRoster] = useState<CareOccupancy[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Care-profile editor (clinical whiteboard) - opened per resident row.
  const [profileFor, setProfileFor] = useState<CareOccupancy | null>(null);

  // Family contacts (VC-11) - opened per resident row, and automatically right
  // after an admit, because adding the daughter is part of admitting the mother.
  const [familyFor, setFamilyFor] = useState<CareOccupancy | null>(null);
  // personRef → how many contacts, and how many are still waiting for consent.
  // One list call for the whole roster rather than one per row; the list view is
  // masked, so this costs no PHI disclosure.
  const [familyCounts, setFamilyCounts] = useState<
    Record<string, { total: number; pending: number }>
  >({});
  // The directory needs its own permission. Rather than guess at the caller's
  // grants, we ask once: if the list is refused, the button is not offered.
  const [canManageFamily, setCanManageFamily] = useState(true);

  // Video-calling SEATS (VC2-6) - the commercial entitlement, separate from the
  // resident's privacy opt-out next to it. personRef → the active seat, so a
  // row can say not just "on" but *who is paying for it*.
  const [seats, setSeats] = useState<Record<string, VideoCallSeat>>({});
  const [seatSummary, setSeatSummary] = useState<VideoCallSeatSummary | null>(
    null
  );
  // Same reasoning as `canManageFamily`: assigning a seat needs
  // `canManageCallContacts`, so a 403 on the roster is an answer, not a fault -
  // hide the column rather than show a control that will always fail.
  const [canManageSeats, setCanManageSeats] = useState(true);
  // Per-row, not the global `busy`: assigning one resident's seat is a network
  // round-trip and must not freeze the discharge button on every other row.
  const [seatBusy, setSeatBusy] = useState<string | null>(null);

  // Admit form (lives in a modal dialog)
  const [admitOpen, setAdmitOpen] = useState(false);
  const [admitRoom, setAdmitRoom] = useState("");
  const [admitName, setAdmitName] = useState("");
  const [admitDob, setAdmitDob] = useState("");
  const [admitMrn, setAdmitMrn] = useState("");
  const [admitEncounter, setAdmitEncounter] = useState("");

  // PHI reveal - keyed by personRef. Each reveal is an audited server call, so
  // we only fetch on explicit operator action and cache the result per row.
  const [revealed, setRevealed] = useState<Record<string, CarePhiReveal>>({});
  const [revealing, setRevealing] = useState<string | null>(null);

  // Åtkomstlogg panel (canViewPhi only). The filters here are not a convenience
  // - they ARE the compliance deliverable: PDL 4 kap. requires the care provider
  // to be able to produce "who accessed resident X between these dates" without
  // asking their software vendor for a database dump.
  const [audit, setAudit] = useState<CareAuditRecord[] | null>(null);
  const [auditPhiOnly, setAuditPhiOnly] = useState(true);
  const [auditFrom, setAuditFrom] = useState("");
  const [auditTo, setAuditTo] = useState("");
  const [auditSubject, setAuditSubject] = useState("");
  const [auditTruncated, setAuditTruncated] = useState(false);
  const [auditChain, setAuditChain] = useState<CareAuditVerification | null>(
    null
  );
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);

  /** Per-resident family-contact counts for the roster badges. */
  const refreshFamilyCounts = useCallback(async () => {
    try {
      const rows = await foyerApi.listCallContacts(premiseId, {
        scope: "person",
      });
      const next: Record<string, { total: number; pending: number }> = {};
      for (const c of rows) {
        if (!c.personRef || c.revokedAt) continue;
        const bucket = (next[c.personRef] ??= { total: 0, pending: 0 });
        bucket.total += 1;
        if (!c.consent) bucket.pending += 1;
      }
      setFamilyCounts(next);
      setCanManageFamily(true);
    } catch {
      // A 403 here is a permissions answer, not an error worth shouting about:
      // this operator manages the roster but not the call directory.
      setFamilyCounts({});
      setCanManageFamily(false);
    }
  }, [premiseId]);

  /**
   * The premise's calling seats: who holds one, and the pool ceiling the
   * operator is working under. Two calls for the whole roster rather than one
   * per row, and both are PHI-free.
   *
   * The summary is session-gated while the roster needs
   * `canManageCallContacts`, so they fail independently: an operator without
   * the permission still gets no column, and a premise that has simply bought
   * nothing still gets an honest "no seats bought" instead of a silent switch.
   */
  const refreshSeats = useCallback(async () => {
    try {
      const rows = await foyerApi.listVideoCallSeats(premiseId);
      const next: Record<string, VideoCallSeat> = {};
      for (const s of rows) {
        if (s.status === "active") next[s.personRef] = s;
      }
      setSeats(next);
      setCanManageSeats(true);
    } catch {
      setSeats({});
      setCanManageSeats(false);
      return;
    }
    try {
      setSeatSummary(await foyerApi.videoCallSeatSummary(premiseId));
    } catch {
      // The ceiling is unknown; the roster above is still true. Leave the last
      // known summary rather than blanking the counts we just rendered.
    }
  }, [premiseId]);

  const refresh = useCallback(async () => {
    void refreshFamilyCounts();
    void refreshSeats();
    try {
      setRoster(await foyerApi.listOccupancy(premiseId));
      setError(null);
      // Collapse any revealed identities - the roster changed and a discharged
      // resident's PHI should not linger on screen.
      setRevealed({});
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [premiseId, refreshFamilyCounts, refreshSeats]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const occupiedRoomIds = useMemo(
    () => new Set(roster.map((o) => o.roomId)),
    [roster]
  );
  const freeRooms = useMemo(
    () => rooms.filter((r) => !occupiedRoomIds.has(r.id)),
    [rooms, occupiedRoomIds]
  );
  const roomLabel = useCallback(
    (id: string) => {
      const r = rooms.find((x) => x.id === id);
      return r?.name ? `${r.name} (${id})` : id;
    },
    [rooms]
  );

  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(label);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const resetAdmitForm = () => {
    setAdmitRoom("");
    setAdmitName("");
    setAdmitDob("");
    setAdmitMrn("");
    setAdmitEncounter("");
  };

  const closeAdmit = () => {
    setAdmitOpen(false);
    resetAdmitForm();
  };

  const admit = () => {
    if (!admitRoom || !admitName.trim()) return;
    const roomId = admitRoom;
    void run("Resident admitted", async () => {
      const created = await foyerApi.admitResident(premiseId, {
        roomId: admitRoom,
        person: {
          name: admitName.trim(),
          dob: admitDob.trim() || null,
          mrn: admitMrn.trim() || null,
        },
        externalEncounterId: admitEncounter.trim() || null,
      });
      // The admit succeeded, so the room is occupied server-side.
      onRoomOccupancyChange?.(roomId, true);
      setAdmitOpen(false);
      resetAdmitForm();
      // Hand straight over to the family contacts rather than leaving it as a
      // second errand: "add the daughter" is part of the admission conversation
      // that is happening right now, and it is the one moment staff have the
      // relative's number in front of them.
      if (canManageFamily && created?.personRef) setFamilyFor(created);
    });
  };

  const discharge = (occ: CareOccupancy) => {
    // Name the consequence that is not obvious from the word "discharge": the
    // family contacts go with the stay (VC-06). Staff who learn that from the
    // empty TV afterwards learn it too late.
    const family = familyCounts[occ.personRef]?.total ?? 0;
    const familyLine = family
      ? ` Their ${family} family contact${family === 1 ? "" : "s"} will be revoked with the stay and disappear from the room's TV.`
      : "";
    if (
      !window.confirm(
        `Discharge the resident in ${roomLabel(occ.roomId)}?${familyLine}`
      )
    )
      return;
    void run("Resident discharged", async () => {
      await foyerApi.dischargeResident(premiseId, occ.id);
      onRoomOccupancyChange?.(occ.roomId, false);
    });
  };

  const setStatus = (occ: CareOccupancy, status: DoorSignStatus) => {
    void run("Status updated", () =>
      foyerApi.setDoorSignStatus(premiseId, occ.roomId, status)
    );
  };

  // Flip the resident-level video-call opt-out straight from the roster (VC-30
  // §4.8) - the privacy control must be reachable here, not only buried inside
  // the clinical care-profile dialog. `videoCallsDisabled` is one OPTIONAL field
  // on a full-PUT profile, so we read the current profile first and re-send it
  // with just that bit flipped; a partial save would blank the clinical fields.
  const setVideoCallsDisabled = (occ: CareOccupancy, disabled: boolean) => {
    void run(
      disabled
        ? "Video calls turned off for this resident"
        : "Video calls turned on for this resident",
      async () => {
        const p = await foyerApi.getCareProfile(premiseId, occ.personRef);
        await foyerApi.saveCareProfile(premiseId, occ.personRef, {
          allergies: p.allergies,
          isolation: p.isolation,
          fallRisk: p.fallRisk,
          diet: p.diet,
          precautions: p.precautions,
          careTeam: p.careTeam,
          notes: p.notes,
          videoCallsDisabled: disabled,
        });
      }
    );
  };

  /**
   * Assign or release this resident's calling seat (VC2-6).
   *
   * Deliberately NOT routed through `run()`: that spins the shared `busy` flag,
   * which disables every control on every row. A seat toggle is one resident's
   * business, so it takes its own per-row spinner and leaves the rest of the
   * roster usable.
   */
  const setSeatAssigned = async (occ: CareOccupancy, assign: boolean) => {
    const seat = seats[occ.personRef];
    // Releasing a FAMILY seat cancels something a relative is being charged
    // for - and the operator cannot give it back, only the family can re-buy
    // it. That asymmetry has to be stated before the click, not after.
    if (!assign && seat?.source === "family") {
      const ok = window.confirm(
        `This seat was bought by the resident's family, not by ${premiseId}. Releasing it stops their subscription and only they can buy it back. Continue?`
      );
      if (!ok) return;
    }
    setSeatBusy(occ.personRef);
    setError(null);
    setNotice(null);
    try {
      if (assign) {
        const res = await foyerApi.assignVideoCallSeat(
          premiseId,
          occ.personRef
        );
        setNotice(
          res.alreadyActive
            ? "This resident already had a calling seat."
            : "Calling seat assigned"
        );
      } else {
        await foyerApi.releaseVideoCallSeat(premiseId, occ.personRef);
        setNotice("Calling seat released");
      }
    } catch (err) {
      const code = (err as { code?: string }).code;
      setError(
        code === "pool_full"
          ? "Every calling seat is assigned. Release one, or buy more seats under Config → Licenses, before assigning another resident."
          : err instanceof Error
            ? err.message
            : String(err)
      );
    } finally {
      setSeatBusy(null);
      // Re-read either way: a failed assign still moves the free-seat count if
      // someone else was assigning at the same time.
      await refreshSeats();
    }
  };

  const changeIdentityMode = (mode: DoorSignIdentityMode) => {
    if (mode === identityMode) return;
    // Confirm before raising public exposure.
    if (EXPOSURE_RANK[mode] > EXPOSURE_RANK[identityMode] && mode !== "none") {
      const ok = window.confirm(
        `This will display resident names on corridor door signs (${reduceDisplayName(
          SAMPLE_NAME,
          mode
        )}). Door signs are visible to anyone in the corridor. Continue?`
      );
      if (!ok) return;
    }
    void run("Identity policy updated", async () => {
      await foyerApi.setDoorSignIdentityMode(premiseId, mode);
      onIdentityModeChange(mode);
    });
  };

  // Reveal one resident's identity. This is an audited PHI read on the server,
  // so it only fires on an explicit click and the result is cached per row.
  const reveal = (occ: CareOccupancy) => {
    if (revealed[occ.personRef] || revealing) return;
    setRevealing(occ.personRef);
    setError(null);
    foyerApi
      .revealPerson(premiseId, occ.personRef)
      .then((phi) => setRevealed((prev) => ({ ...prev, [occ.personRef]: phi })))
      .catch((err) =>
        setError(err instanceof Error ? err.message : String(err))
      )
      .finally(() => setRevealing(null));
  };

  const hide = (personRef: string) =>
    setRevealed((prev) => {
      const next = { ...prev };
      delete next[personRef];
      return next;
    });

  /** The current filter set, shared by the table, the CSV link and verification. */
  const auditFilter = useMemo(
    (): CareAuditFilter => ({
      phiOnly: auditPhiOnly,
      // `date` inputs give a local calendar day; widen `to` to the END of that
      // day so "1 Jan → 31 Jan" includes the 31st. An off-by-one-day export is a
      // wrong answer to a regulator, not a cosmetic bug.
      from: auditFrom ? new Date(`${auditFrom}T00:00:00`).toISOString() : "",
      to: auditTo
        ? new Date(
            new Date(`${auditTo}T00:00:00`).getTime() + 24 * 60 * 60 * 1000
          ).toISOString()
        : "",
      subjectRef: auditSubject.trim(),
    }),
    [auditPhiOnly, auditFrom, auditTo, auditSubject]
  );

  const loadAudit = useCallback(
    async (filter: CareAuditFilter) => {
      setAuditLoading(true);
      setAuditError(null);
      try {
        const page = await foyerApi.listCareAudit(premiseId, {
          ...filter,
          limit: 500,
        });
        setAudit(page.rows);
        setAuditTruncated(page.truncated);
        // Verify the chain on every load rather than behind another button: a
        // tamper indicator nobody clicks is not a control.
        setAuditChain(
          await foyerApi.verifyCareAudit(premiseId, {
            from: filter.from || undefined,
            to: filter.to || undefined,
          })
        );
      } catch (err) {
        setAuditError(err instanceof Error ? err.message : String(err));
      } finally {
        setAuditLoading(false);
      }
    },
    [premiseId]
  );

  return (
    <Stack spacing={3}>
      <PageHeader eyebrow="Care" title="Residents & door signs" />

      {error && <Alert severity="error">{error}</Alert>}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {/* Identity policy */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Door-sign identity policy
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl
              size="small"
              sx={{ minWidth: 320 }}
              disabled={!canViewPhi || busy}
            >
              <InputLabel id="identity-mode">Show on door signs</InputLabel>
              <Select
                labelId="identity-mode"
                label="Show on door signs"
                value={identityMode}
                onChange={(e) =>
                  changeIdentityMode(e.target.value as DoorSignIdentityMode)
                }
                data-testid="identity-mode-select"
              >
                {IDENTITY_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!canViewPhi && (
              <Typography variant="caption" color="text.secondary">
                Requires PHI access to change.
              </Typography>
            )}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            Premise-wide. Default is <strong>Nothing</strong> - a name in a
            public corridor is a deliberate disclosure.
          </Typography>
        </CardContent>
      </Card>

      {/* Roster */}
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">Current residents</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<LoginIcon />}
              onClick={() => setAdmitOpen(true)}
              data-testid="admit-open"
            >
              Admit resident
            </Button>
          </Stack>
          {/* The ceiling the operator is working under, stated before they
              reach for a switch - "why is this greyed out" should never need a
              trip to another page to answer. Also names the second condition:
              a seat alone does not put the tile on a resident's TV if their
              own opt-out is off. */}
          {canManageSeats && seatSummary && roster.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1.5 }}
              data-testid="calling-seat-pool"
            >
              {poolCopy(seatSummary)} A resident can be called when they hold a
              seat <em>and</em> their calls are allowed.
            </Typography>
          )}
          {roster.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No residents admitted yet.
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" data-testid="roster-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Room</TableCell>
                    <TableCell>Shown on sign</TableCell>
                    {canViewPhi && (
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          Identity
                          <Tooltip title="Revealing an identity is recorded in the compliance audit trail.">
                            <GavelIcon
                              fontSize="inherit"
                              color="action"
                              sx={{ fontSize: 14 }}
                            />
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    )}
                    <TableCell>Status</TableCell>
                    {canManageSeats && (
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          Calling seat
                          <Tooltip title="The paid entitlement. A seat comes either from the seats this site bought (operator) or from one a relative bought themselves (family). Seats follow the resident and are freed on discharge.">
                            <VideocamIcon
                              fontSize="inherit"
                              color="action"
                              sx={{ fontSize: 14 }}
                            />
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    )}
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        Calls allowed
                        <Tooltip title="A resident-level opt-out. When off, this resident cannot be called by anyone (including staff) and cannot call out - it overrides premise policy and every contact.">
                          <VideocamOffIcon
                            fontSize="inherit"
                            color="action"
                            sx={{ fontSize: 14 }}
                          />
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>Admitted</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roster.map((occ) => (
                    <TableRow
                      key={occ.id}
                      data-testid={`roster-row-${occ.roomId}`}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {roomLabel(occ.roomId)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {occ.doorSignDisplayName ? (
                          <Chip size="small" label={occ.doorSignDisplayName} />
                        ) : (
                          <Tooltip title="Hidden by the identity policy">
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      {canViewPhi && (
                        <TableCell data-testid={`identity-${occ.roomId}`}>
                          {revealed[occ.personRef] ? (
                            <Stack spacing={0.25}>
                              <Typography variant="body2" fontWeight={500}>
                                {revealed[occ.personRef].name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {revealed[occ.personRef].dob
                                  ? `DOB ${revealed[occ.personRef].dob}`
                                  : "DOB -"}
                                {revealed[occ.personRef].mrn
                                  ? ` · MRN ${revealed[occ.personRef].mrn}`
                                  : ""}
                              </Typography>
                              <Button
                                size="small"
                                variant="text"
                                sx={{
                                  alignSelf: "flex-start",
                                  minWidth: 0,
                                  px: 0,
                                }}
                                onClick={() => hide(occ.personRef)}
                              >
                                Hide
                              </Button>
                            </Stack>
                          ) : (
                            <Tooltip title="Decrypts the resident's identity. This action is logged for compliance.">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={
                                    revealing === occ.personRef ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <VisibilityIcon />
                                    )
                                  }
                                  onClick={() => reveal(occ)}
                                  disabled={revealing === occ.personRef}
                                  data-testid={`reveal-${occ.roomId}`}
                                >
                                  Show identity
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Select
                          size="small"
                          value={occ.doorSignStatus}
                          onChange={(e) =>
                            setStatus(occ, e.target.value as DoorSignStatus)
                          }
                          disabled={busy}
                          data-testid={`status-${occ.roomId}`}
                          sx={{ minWidth: 160 }}
                        >
                          {STATUS_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      {canManageSeats && (
                        <TableCell data-testid={`calling-seat-${occ.roomId}`}>
                          <SeatCell
                            roomId={occ.roomId}
                            seat={seats[occ.personRef] ?? null}
                            summary={seatSummary}
                            busy={seatBusy === occ.personRef}
                            onChange={(assign) =>
                              void setSeatAssigned(occ, assign)
                            }
                          />
                        </TableCell>
                      )}
                      <TableCell data-testid={`video-calls-${occ.roomId}`}>
                        <Tooltip
                          title={
                            occ.videoCallsDisabled
                              ? "Video calls are OFF for this resident. Toggle on to allow calls again."
                              : "Video calls are allowed. Toggle off to stop all calls for this resident - overrides premise policy and every contact."
                          }
                        >
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                size="small"
                                color={
                                  occ.videoCallsDisabled ? "error" : "success"
                                }
                                checked={!occ.videoCallsDisabled}
                                onChange={(e) =>
                                  setVideoCallsDisabled(occ, !e.target.checked)
                                }
                                disabled={busy}
                                data-testid={`video-calls-switch-${occ.roomId}`}
                              />
                            }
                            label={
                              occ.videoCallsDisabled ? (
                                <Chip
                                  size="small"
                                  color="error"
                                  icon={<VideocamOffIcon />}
                                  label="Off"
                                  data-testid={`video-calls-off-${occ.roomId}`}
                                />
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  On
                                </Typography>
                              )
                            }
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(occ.admittedAt).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          {canManageFamily &&
                            (() => {
                              const counts = familyCounts[occ.personRef];
                              const pending = counts?.pending ?? 0;
                              return (
                                <Tooltip
                                  title={
                                    pending > 0
                                      ? `${pending} contact${
                                          pending === 1 ? "" : "s"
                                        } waiting for consent - inactive until it is recorded.`
                                      : "Relatives this resident can call, and who may ring the room. Removed on discharge."
                                  }
                                >
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color={pending > 0 ? "warning" : "primary"}
                                    startIcon={<FamilyIcon />}
                                    onClick={() => setFamilyFor(occ)}
                                    disabled={busy}
                                    data-testid={`family-contacts-${occ.roomId}`}
                                  >
                                    Family
                                    {counts?.total
                                      ? ` (${counts.total}${
                                          pending > 0 ? ` · ${pending}!` : ""
                                        })`
                                      : ""}
                                  </Button>
                                </Tooltip>
                              );
                            })()}
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<MedicalIcon />}
                            onClick={() => setProfileFor(occ)}
                            disabled={busy}
                            data-testid={`care-profile-${occ.roomId}`}
                          >
                            Care profile
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<LogoutIcon />}
                            onClick={() => discharge(occ)}
                            disabled={busy}
                            data-testid={`discharge-${occ.roomId}`}
                          >
                            Discharge
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Care-profile editor (clinical whiteboard) */}
      {profileFor && (
        <CareProfileDialog
          open={!!profileFor}
          premiseId={premiseId}
          personRef={profileFor.personRef}
          roomLabel={roomLabel(profileFor.roomId)}
          onClose={() => setProfileFor(null)}
          onSaved={() => setNotice("Care profile saved")}
        />
      )}

      {/* Family contacts (VC-11) - person-scoped, revoked with the stay. */}
      {familyFor && (
        <FamilyContactsDialog
          open={!!familyFor}
          premiseId={premiseId}
          personRef={familyFor.personRef}
          roomLabel={roomLabel(familyFor.roomId)}
          defaultTz={premiseTimezone || "Europe/Stockholm"}
          canViewPhi={canViewPhi}
          onClose={() => setFamilyFor(null)}
          onChanged={() => void refreshFamilyCounts()}
        />
      )}

      {/* Admit dialog */}
      <Dialog
        open={admitOpen}
        onClose={closeAdmit}
        fullWidth
        maxWidth="sm"
        data-testid="admit-dialog"
      >
        <DialogTitle>Admit a resident</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="admit-room">Room</InputLabel>
              <Select
                labelId="admit-room"
                label="Room"
                value={admitRoom}
                onChange={(e) => setAdmitRoom(e.target.value)}
                data-testid="admit-room"
              >
                {freeRooms.length === 0 && (
                  <MenuItem value="" disabled>
                    No free rooms
                  </MenuItem>
                )}
                {freeRooms.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {roomLabel(r.id)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Resident name"
              value={admitName}
              onChange={(e) => setAdmitName(e.target.value)}
              fullWidth
              autoFocus
              inputProps={{ "data-testid": "admit-name" }}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                size="small"
                type="date"
                label="DOB (optional)"
                value={admitDob}
                onChange={(e) => setAdmitDob(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ "data-testid": "admit-dob" }}
              />
              <TextField
                size="small"
                label="MRN (optional)"
                value={admitMrn}
                onChange={(e) => setAdmitMrn(e.target.value)}
                fullWidth
              />
            </Stack>
            <TextField
              size="small"
              label="Encounter ID (optional)"
              value={admitEncounter}
              onChange={(e) => setAdmitEncounter(e.target.value)}
              fullWidth
              inputProps={{ "data-testid": "admit-encounter" }}
            />
            <Typography variant="caption" color="text.secondary">
              Name, DOB and MRN are encrypted on entry and never read back on
              this roster - only the policy-reduced display name is shown.
              Re-admitting a resident with a known MRN reuses their record.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdmit} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={admit}
            disabled={busy || !admitRoom || !admitName.trim()}
            data-testid="admit-submit"
          >
            {busy ? "Admitting…" : "Admit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit compliance panel - PHI access leaves a trail; surface it here so
          operators can see who read what without leaving the CMS. */}
      {canViewPhi && (
        <CollapsiblePanel
          title="Compliance audit trail"
          defaultExpanded={false}
          testId="care-audit-panel"
        >
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
            >
              <TextField
                size="small"
                type="date"
                label="From"
                value={auditFrom}
                onChange={(e) => setAuditFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ "data-testid": "audit-from" }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={auditTo}
                onChange={(e) => setAuditTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ "data-testid": "audit-to" }}
              />
              <TextField
                size="small"
                label="Resident (personRef)"
                placeholder="all residents"
                value={auditSubject}
                onChange={(e) => setAuditSubject(e.target.value)}
                inputProps={{ "data-testid": "audit-subject" }}
                sx={{ minWidth: 220 }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<GavelIcon />}
                onClick={() => void loadAudit(auditFilter)}
                disabled={auditLoading}
                data-testid="audit-load"
              >
                {audit === null ? "Load access log" : "Refresh"}
              </Button>
              <Button
                size="small"
                variant="contained"
                component="a"
                href={foyerApi.careAuditExportUrl(premiseId, auditFilter)}
                data-testid="audit-export"
              >
                Export CSV
              </Button>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={auditPhiOnly}
                    onChange={(e) => {
                      setAuditPhiOnly(e.target.checked);
                      if (audit !== null) {
                        void loadAudit({
                          ...auditFilter,
                          phiOnly: e.target.checked,
                        });
                      }
                    }}
                    data-testid="audit-phi-only"
                  />
                }
                label="Patient data only"
              />
              {auditLoading && <CircularProgress size={16} />}
            </Stack>

            {auditError && <Alert severity="error">{auditError}</Alert>}

            {/* Never let a capped page pass for a complete one - the user is
                about to hand this to an inspector. */}
            {auditTruncated && (
              <Alert severity="warning" data-testid="audit-truncated">
                More events matched than are shown. Narrow the date range before
                treating this as a complete log - the CSV export covers the full
                range.
              </Alert>
            )}

            {/* Tamper-evidence verdict. Loud when broken, quiet when intact. */}
            {auditChain &&
              (auditChain.ok ? (
                <Alert severity="success" data-testid="audit-chain-ok">
                  Log integrity verified - {auditChain.checked} entries form an
                  unbroken chain (head #{auditChain.headSeq},{" "}
                  {auditChain.headHash.slice(0, 16)}…). Record this digest to
                  detect later changes.
                </Alert>
              ) : auditChain.breakAt?.reason === "reorder_window_exceeded" ? (
                // NOT a tamper finding: the verifier could not put the trail
                // back into chain order, so it stopped rather than guess. Saying
                // "this log has been altered" here would send a compliance
                // officer chasing a defect in our reader.
                <Alert
                  severity="warning"
                  data-testid="audit-chain-inconclusive"
                >
                  Integrity check inconclusive - the log could not be read in
                  chain order past entry #{auditChain.breakAt?.seq}, so it was
                  stopped rather than judged. This says nothing about whether
                  the log is intact. Narrow the date range and re-run; if it
                  repeats, report it - the limitation is ours, not the
                  log&apos;s.
                </Alert>
              ) : (
                <Alert severity="error" data-testid="audit-chain-broken">
                  Log integrity check FAILED at entry #{auditChain.breakAt?.seq}{" "}
                  ({auditChain.breakAt?.reason}). This access log has been
                  altered or is incomplete - escalate before relying on it.
                </Alert>
              ))}

            {/* What the verdict above does not reach. Rows written before the
                hash chain shipped carry no digest, so they can be neither
                confirmed nor faulted - and a green "integrity verified" that
                quietly covered them would overclaim in the one place this
                product must not: the answer a care provider gives an
                inspector. Stated separately rather than folded into the
                verdict, because it is not a finding. */}
            {auditChain !== null && auditChain.unchained > 0 && (
              <Alert severity="info" data-testid="audit-chain-unchained">
                {auditChain.unchained} older{" "}
                {auditChain.unchained === 1 ? "entry" : "entries"} in this range
                predate tamper-evidence and carry no digest, so the check above
                does not cover {auditChain.unchained === 1 ? "it" : "them"}.
                They are shown and exported as normal - nothing is missing, and
                nothing suggests they were altered.
              </Alert>
            )}

            {audit !== null &&
              (audit.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No audit events recorded.
                </Typography>
              ) : (
                <Table size="small" data-testid="audit-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Actor</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Resource</TableCell>
                      <TableCell>Outcome</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {audit.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Typography variant="caption">
                            {new Date(row.at).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.actor}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <Typography variant="body2">
                              {row.action}
                            </Typography>
                            {row.phi && (
                              <Chip size="small" color="warning" label="PHI" />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {row.resourceType}
                            {row.resourceId ? ` · ${row.resourceId}` : ""}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={
                              row.outcome === "success"
                                ? "success"
                                : row.outcome === "denied"
                                  ? "error"
                                  : "default"
                            }
                            label={row.outcome}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ))}
          </Stack>
        </CollapsiblePanel>
      )}
    </Stack>
  );
}

/** The pool line above the roster: bought / assigned / left. */
function poolCopy(s: VideoCallSeatSummary): string {
  if (s.unlimited) {
    return "Site license: every resident is entitled to video calling, no seat needed.";
  }
  if (s.poolSeats === 0) {
    return s.familySeats > 0
      ? `No calling seats bought for this site - only the ${s.familySeats} family-funded seat${
          s.familySeats === 1 ? "" : "s"
        } work${s.familySeats === 1 ? "s" : ""}. Buy seats under Config → Licenses.`
      : "No calling seats bought for this site. Buy them under Config → Licenses.";
  }
  const family = s.familySeats
    ? ` (+${s.familySeats} family-funded, outside the pool)`
    : "";
  return `Calling seats: ${s.assignedFromPool} of ${s.poolSeats} assigned, ${s.freeSeats} free${family}.`;
}

/**
 * One resident's seat control.
 *
 * Three shapes, because the switch is only honest in one of them:
 *   - **Site license** - entitlement comes from the pool being unlimited, so a
 *     per-resident seat would change nothing. A switch here would be a control
 *     that does not control anything; show the fact instead.
 *   - **Holds a seat** - on, plus WHO funds it. A family seat is not the
 *     operator's to give back (VC2-6), so it must never look like a plain
 *     operator toggle.
 *   - **No seat** - on if the pool has one to draw, greyed with the reason
 *     when it does not. Blocking here rather than letting the click 409 keeps
 *     "the pool is full" a fact the operator reads, not an error they hit.
 */
function SeatCell({
  roomId,
  seat,
  summary,
  busy,
  onChange,
}: {
  roomId: string;
  seat: VideoCallSeat | null;
  summary: VideoCallSeatSummary | null;
  busy: boolean;
  onChange: (assign: boolean) => void;
}) {
  if (summary?.unlimited) {
    return (
      <Tooltip title="This site has a video-calling site license - every resident is entitled, so no seat is assigned individually.">
        <Chip
          size="small"
          variant="outlined"
          color="success"
          label="Site license"
          data-testid={`calling-seat-unlimited-${roomId}`}
        />
      </Tooltip>
    );
  }

  const family = seat?.source === "family";
  const poolEmpty = !!summary && summary.poolSeats === 0;
  const poolFull = !!summary && !summary.hasFreeSeats;
  // A resident who already holds a seat can always be switched OFF - the block
  // is on drawing a NEW seat, never on giving one back.
  const blocked = !seat && (poolEmpty || poolFull);

  const title = seat
    ? family
      ? "Funded by the resident's family. Switching off stops their subscription - only they can buy it back."
      : "Holds one of this site's calling seats. Switching off returns it to the pool."
    : poolEmpty
      ? "No calling seats bought for this site yet - buy them under Config → Licenses."
      : poolFull
        ? `All ${summary?.poolSeats} seats are assigned. Release one, or buy more under Config → Licenses.`
        : "Give this resident a calling seat from the site's pool.";

  return (
    <Tooltip title={title}>
      <FormControlLabel
        sx={{ m: 0 }}
        control={
          busy ? (
            <Box
              sx={{
                width: 40,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size={14} />
            </Box>
          ) : (
            <Switch
              size="small"
              color={family ? "info" : "success"}
              checked={!!seat}
              onChange={(e) => onChange(e.target.checked)}
              disabled={blocked}
              // On the INPUT, not the wrapper: `disabled`/`checked` live there,
              // so a test asserting either is asserting the real control.
              inputProps={
                {
                  "data-testid": `calling-seat-switch-${roomId}`,
                } as Record<string, string>
              }
            />
          )
        }
        label={
          seat ? (
            <Chip
              size="small"
              variant="outlined"
              color={family ? "info" : "success"}
              label={family ? "Family-funded" : "Operator"}
              data-testid={`calling-seat-source-${roomId}`}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {blocked ? (poolEmpty ? "No seats bought" : "Pool full") : "None"}
            </Typography>
          )
        }
      />
    </Tooltip>
  );
}
